import React from 'react';
import styled from 'styled-components';
import {
  colors,
  fonts,
  breakpoints,
  radii,
  shadows,
  headingStyle,
  sectionPaddingStyle,
  containerStyle,
  icones,
} from '../annuaire/designSystem';
import { getShopUrl } from '../../utils/utils';

const Section = styled.section`
  margin: 0;
  ${sectionPaddingStyle}
  background-color: ${colors.white};
`;

const Container = styled.div`
  ${containerStyle}
`;

const Heading = styled.h2`
  ${headingStyle}
  font-size: 1.5rem;
  margin-bottom: 32px;

  @media (min-width: ${breakpoints.mobile}) {
    font-size: 2.125rem;
  }
`;

const Emphasis = styled.span`
  color: ${colors.bleu};
`;

const ShopsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 24px;
`;

const ShopCard = styled.a`
  background-color: ${colors.white};
  border-radius: ${radii.card};
  overflow: hidden;
  box-shadow: ${shadows.card};
  transition: all 0.2s ease;
  text-decoration: none;
  display: block;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 6px 16px rgba(49, 73, 89, 0.12);
  }
`;

const ShopCardImage = styled.div`
  background: linear-gradient(
    135deg,
    ${colors.bleuPastel} 0%,
    ${colors.violetPastel} 100%
  );
  height: 150px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  position: relative;
`;

const ShopPhoto = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  position: absolute;
  top: 0;
  left: 0;
`;

const ShopPlaceholder = styled.img`
  width: 56px;
  height: auto;
  opacity: 0.4;
`;

const ShopCardContent = styled.div`
  padding: 20px;
`;

const ShopName = styled.h3`
  font-family: ${fonts.body};
  font-weight: 600;
  font-size: 1rem;
  margin-bottom: 8px;
  color: ${colors.textDark};
`;

const ShopRating = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  margin-bottom: 12px;
  font-family: ${fonts.body};
  font-size: 0.85rem;
  color: ${colors.textDark};
`;

const Star = styled.span`
  color: #f59e0b;
  font-size: 0.9rem;
`;

const ShopAddress = styled.p`
  display: flex;
  gap: 6px;
  font-family: ${fonts.body};
  font-size: 0.85rem;
  color: ${colors.textSecondary};
  margin: 0;
`;

const AddressIcon = styled.img`
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  object-fit: contain;
`;

export default function SimilarShops({ similarShops, city }) {
  if (!similarShops || similarShops.length === 0) return null;

  return (
    <Section>
      <Container>
        <Heading>
          Autres ateliers v&eacute;lo &agrave; <Emphasis>{city || ''}</Emphasis>
        </Heading>
        <ShopsGrid>
          {similarShops.slice(0, 3).map((s, i) => {
            const rawDetails = s.details || {};
            const details = Array.isArray(rawDetails)
              ? rawDetails[0] || {}
              : rawDetails;
            const photos = details.photos || {};
            const photo = photos.s3_main || null;
            const noteGoogle = details.note_google || s.note_google;
            const addr = s.address || s.adresse_complete || '';
            const shopUrl = getShopUrl(s);

            return (
              <ShopCard key={s.id || i} href={shopUrl}>
                <ShopCardImage>
                  {photo ? (
                    <ShopPhoto src={photo} alt={s.name || ''} loading="lazy" />
                  ) : (
                    <ShopPlaceholder src={icones.velo} alt="V&eacute;lo" />
                  )}
                </ShopCardImage>
                <ShopCardContent>
                  <ShopName>{s.name || s.nom || ''}</ShopName>
                  {noteGoogle && (
                    <ShopRating>
                      <Star>★</Star>
                      <span>{noteGoogle}</span>
                    </ShopRating>
                  )}
                  {addr && (
                    <ShopAddress>
                      <AddressIcon src={icones.localisation} alt="" />
                      <span>{addr}</span>
                    </ShopAddress>
                  )}
                </ShopCardContent>
              </ShopCard>
            );
          })}
        </ShopsGrid>
      </Container>
    </Section>
  );
}
